import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SpeedIcon from '@mui/icons-material/Speed';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PreviewIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { GlobalStyles } from '@mui/material';
import { comunicazioniApi, plantsApi, metersApi } from '../../services/api';

// Registra dimensioni font personalizzate in Quill
const Size = Quill.import('formats/size');
const FONT_SIZES = ['8px', '9px', '10px', '11px', '12px', '13px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px'];
Size.whitelist = FONT_SIZES;
Quill.register(Size, true);

// Registra interlinea personalizzata (genera style="line-height: X" inline)
const Parchment = Quill.import('parchment');
const LINE_HEIGHTS = ['1', '1.2', '1.4', '1.6', '1.8', '2', '2.4', '2.8', '3'];
const lineHeightStyle = new Parchment.Attributor.Style('lineheight', 'line-height', {
  scope: Parchment.Scope.INLINE,
  whitelist: LINE_HEIGHTS
});
Quill.register(lineHeightStyle, true);

// CSS globale per le classi ql-size-* e ql-lineheight generate da Quill
const quillSizeStyles = {
  ...FONT_SIZES.reduce((acc, s) => {
    acc[`.ql-size-${s}`] = { fontSize: `${s} !important` };
    acc[`.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${s}"]::before`] = { content: `'${s}'` };
    acc[`.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${s}"]::before`] = { content: `'${s}'`, fontSize: s };
    return acc;
  }, {}),
  // Picker interlinea
  '.ql-snow .ql-picker.ql-lineheight': { width: '85px' },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-label::before': { content: "'Interl.'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="1"]::before': { content: "'1× (compatta)'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="1.2"]::before': { content: "'1.2×'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="1.4"]::before': { content: "'1.4×'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="1.6"]::before': { content: "'1.6×'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="1.8"]::before': { content: "'1.8× (normal)'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="2"]::before': { content: "'2×'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="2.4"]::before': { content: "'2.4×'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="2.8"]::before': { content: "'2.8×'" },
  '.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="3"]::before': { content: "'3× (larga)'" },
};

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    [{ 'size': [false, ...FONT_SIZES] }, { 'lineheight': LINE_HEIGHTS }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    [{ 'color': [] }, { 'background': [] }],
    ['blockquote'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'size', 'lineheight',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'align',
  'color', 'background',
  'blockquote'
];

const CommunicationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // Form state
  const [titolo, setTitolo] = useState('');
  const [contenuto, setContenuto] = useState('');
  const [attivo, setAttivo] = useState(true);
  const [mostraQrcode, setMostraQrcode] = useState(false);

  // Target selection state
  // selectedPlants: { [plantId]: 'all' | 'partial' } — 'all' = intero impianto, 'partial' = solo alcuni contatori
  // selectedMeters: { [meterId]: true } — singoli contatori selezionati
  const [selectedPlants, setSelectedPlants] = useState({});
  const [selectedMeters, setSelectedMeters] = useState({});

  const [saveError, setSaveError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Load existing communication for edit
  const { data: commData, isLoading: commLoading } = useQuery({
    queryKey: ['communication', id],
    queryFn: () => comunicazioniApi.getById(id).then(r => r.data),
    enabled: isEdit
  });

  // Load all plants (with meters)
  const { data: plantsData, isLoading: plantsLoading } = useQuery({
    queryKey: ['plants-all-for-comm'],
    queryFn: () => plantsApi.getAll({ limit: 1000, attivo: true }).then(r => r.data)
  });

  // Load all meters
  const { data: metersData, isLoading: metersLoading } = useQuery({
    queryKey: ['meters-all-for-comm'],
    queryFn: () => metersApi.getAll({ limit: 5000 }).then(r => r.data)
  });

  const plants = plantsData?.data || [];
  const allMeters = metersData?.data || [];

  // Organizza contatori per impianto
  const metersByPlant = useMemo(() => {
    const map = {};
    for (const m of allMeters) {
      const pid = m.plant_id;
      if (!pid) continue;
      if (!map[pid]) map[pid] = [];
      map[pid].push(m);
    }
    return map;
  }, [allMeters]);

  // Populate form from existing communication
  useEffect(() => {
    if (commData?.data) {
      const comm = commData.data;
      setTitolo(comm.titolo || '');
      setContenuto(comm.contenuto || '');
      setAttivo(!!comm.attivo);
      setMostraQrcode(!!comm.mostra_qrcode);

      // Rebuild selection from targets
      const pSel = {};
      const mSel = {};
      const targets = comm.targets || [];

      // Group targets by plant_id
      const targetsByPlant = {};
      for (const t of targets) {
        if (t.plant_id) {
          if (!targetsByPlant[t.plant_id]) targetsByPlant[t.plant_id] = [];
          targetsByPlant[t.plant_id].push(t);
        }
      }

      for (const [plantId, plantTargets] of Object.entries(targetsByPlant)) {
        const hasPlantOnly = plantTargets.some(t => !t.meter_id);
        if (hasPlantOnly) {
          // Intero impianto selezionato
          pSel[plantId] = 'all';
        } else {
          // Solo alcuni contatori
          pSel[plantId] = 'partial';
          for (const t of plantTargets) {
            if (t.meter_id) mSel[t.meter_id] = true;
          }
        }
      }

      setSelectedPlants(pSel);
      setSelectedMeters(mSel);
    }
  }, [commData]);

  // Gestione selezione impianto (checkbox header impianto)
  const handlePlantToggle = (plantId) => {
    setSelectedPlants(prev => {
      const next = { ...prev };
      if (next[plantId]) {
        // Deseleziona impianto e tutti i suoi contatori
        delete next[plantId];
        setSelectedMeters(prevM => {
          const nextM = { ...prevM };
          const plantMeters = metersByPlant[plantId] || [];
          for (const m of plantMeters) delete nextM[m.id];
          return nextM;
        });
      } else {
        // Seleziona intero impianto
        next[plantId] = 'all';
        // Rimuovi selezioni individuali dei contatori (non servono se si seleziona tutto l'impianto)
        setSelectedMeters(prevM => {
          const nextM = { ...prevM };
          const plantMeters = metersByPlant[plantId] || [];
          for (const m of plantMeters) delete nextM[m.id];
          return nextM;
        });
      }
      return next;
    });
  };

  // Gestione selezione singolo contatore
  const handleMeterToggle = (meterId, plantId) => {
    setSelectedMeters(prev => {
      const nextM = { ...prev };
      if (nextM[meterId]) {
        delete nextM[meterId];
      } else {
        nextM[meterId] = true;
      }

      // Aggiorna stato impianto
      const plantMeters = metersByPlant[plantId] || [];
      const selectedCount = plantMeters.filter(m => nextM[m.id]).length;

      setSelectedPlants(prevP => {
        const nextP = { ...prevP };
        if (selectedCount === 0) {
          delete nextP[plantId];
        } else if (selectedCount === plantMeters.length) {
          nextP[plantId] = 'all';
        } else {
          nextP[plantId] = 'partial';
        }
        return nextP;
      });

      return nextM;
    });
  };

  // Controlla se un contatore è selezionato (direttamente o via impianto intero)
  const isMeterSelected = (meterId, plantId) => {
    if (selectedPlants[plantId] === 'all') return true;
    return !!selectedMeters[meterId];
  };

  // Costruisci array targets per il backend
  const buildTargets = () => {
    const targets = [];
    for (const [plantId, mode] of Object.entries(selectedPlants)) {
      if (mode === 'all') {
        // Intero impianto
        targets.push({ plant_id: parseInt(plantId), meter_id: null });
      } else if (mode === 'partial') {
        // Solo contatori selezionati
        const plantMeters = metersByPlant[plantId] || [];
        for (const m of plantMeters) {
          if (selectedMeters[m.id]) {
            targets.push({ plant_id: parseInt(plantId), meter_id: m.id });
          }
        }
      }
    }
    return targets;
  };

  const saveMutation = useMutation({
    mutationFn: (data) => isEdit
      ? comunicazioniApi.update(id, data)
      : comunicazioniApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications'] });
      navigate('/crm/comunicazioni');
    },
    onError: (err) => {
      setSaveError(err.response?.data?.message || err.message);
    }
  });

  const handleSave = () => {
    if (!titolo.trim()) {
      setSaveError('Il titolo è obbligatorio');
      return;
    }
    if (!contenuto.trim() || contenuto === '<p><br></p>') {
      setSaveError('Il contenuto è obbligatorio');
      return;
    }

    setSaveError(null);
    saveMutation.mutate({
      titolo: titolo.trim(),
      contenuto,
      attivo,
      mostra_qrcode: mostraQrcode,
      targets: buildTargets()
    });
  };

  if (isEdit && commLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <GlobalStyles styles={quillSizeStyles} />
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/crm/comunicazioni')}
        >
          Indietro
        </Button>
        <Typography variant="h5" fontWeight={700}>
          {isEdit ? 'Modifica Comunicazione' : 'Nuova Comunicazione'}
        </Typography>
      </Box>

      {saveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>}

      {/* Form principale */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Dettagli comunicazione</Typography>

        <TextField
          label="Titolo"
          value={titolo}
          onChange={(e) => setTitolo(e.target.value)}
          fullWidth
          required
          sx={{ mb: 2 }}
          placeholder="Es: Avviso cambio prezzo GPL, Comunicazione manutenzione..."
        />

        <FormControlLabel
          control={<Switch checked={attivo} onChange={(e) => setAttivo(e.target.checked)} color="primary" />}
          label={attivo ? 'Comunicazione attiva (verrà inclusa nei PDF)' : 'Comunicazione disattivata'}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={mostraQrcode}
              onChange={(e) => setMostraQrcode(e.target.checked)}
              color="success"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCode2Icon fontSize="small" sx={{ color: mostraQrcode ? 'success.main' : 'text.disabled' }} />
              <Box>
                <Typography variant="body2" component="span" fontWeight={mostraQrcode ? 600 : 400}>
                  {mostraQrcode ? 'Box QR Code WhatsApp incluso nel PDF' : 'Box QR Code WhatsApp non incluso'}
                </Typography>
                {mostraQrcode && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Il QR verrà generato con i dati del cliente (richiede numero WhatsApp azienda + CF/P.IVA cliente)
                  </Typography>
                )}
              </Box>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Contenuto della comunicazione (verrà inserito come prima pagina del PDF fattura)
        </Typography>
        <Box sx={{ '.ql-editor': { minHeight: 200 }, mb: 2 }}>          
          <ReactQuill
            theme="snow"
            value={contenuto}
            onChange={setContenuto}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Scrivi il contenuto della comunicazione..."
          />
        </Box>
      </Paper>

      {/* Sezione Destinatari */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Destinatari
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleziona gli impianti e/o i contatori ai quali inviare questa comunicazione.
          Se non selezioni nessun impianto, la comunicazione verrà allegata a tutte le fatture.
          Espandi un impianto per selezionare solo specifici contatori.
        </Typography>

        {Object.keys(selectedPlants).length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(selectedPlants).map(([plantId, mode]) => {
              const plant = plants.find(p => p.id === parseInt(plantId));
              const meterCount = mode === 'partial'
                ? (metersByPlant[plantId] || []).filter(m => selectedMeters[m.id]).length
                : (metersByPlant[plantId] || []).length;
              return (
                <Chip
                  key={plantId}
                  icon={<LocalGasStationIcon />}
                  label={`${plant?.plant_code || plantId} - ${mode === 'all' ? 'Tutti' : `${meterCount} contatori`}`}
                  color="primary"
                  variant="outlined"
                  onDelete={() => handlePlantToggle(parseInt(plantId))}
                />
              );
            })}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {plantsLoading || metersLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 1 }}>Caricamento impianti...</Typography>
          </Box>
        ) : plants.length === 0 ? (
          <Alert severity="info">Nessun impianto attivo trovato.</Alert>
        ) : (
          <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
            {plants.map((plant) => {
              const plantMeters = metersByPlant[plant.id] || [];
              const isPlantSelected = !!selectedPlants[plant.id];
              const isAllSelected = selectedPlants[plant.id] === 'all';
              const isPartial = selectedPlants[plant.id] === 'partial';

              return (
                <Accordion
                  key={plant.id}
                  sx={{
                    border: isPlantSelected ? '1px solid' : '1px solid transparent',
                    borderColor: isPlantSelected ? 'primary.main' : 'divider',
                    '&:before': { display: 'none' },
                    mb: 0.5
                  }}
                >
                  <AccordionSummary
                    expandIcon={plantMeters.length > 0 ? <ExpandMoreIcon /> : null}
                    sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isPartial}
                      onChange={(e) => {
                        e.stopPropagation();
                        handlePlantToggle(plant.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      color="primary"
                    />
                    <LocalGasStationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Box>
                      <Typography fontWeight={600} variant="body2">
                        {plant.plant_code} {plant.nome ? `- ${plant.nome}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {plant.indirizzo ? `${plant.indirizzo}, ` : ''}{plant.citta || ''}
                        {plantMeters.length > 0 ? ` · ${plantMeters.length} contatori` : ' · Nessun contatore'}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  {plantMeters.length > 0 && (
                    <AccordionDetails sx={{ pl: 6 }}>
                      {plantMeters.map((meter) => (
                        <Box
                          key={meter.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          <Checkbox
                            checked={isMeterSelected(meter.id, plant.id)}
                            onChange={() => handleMeterToggle(meter.id, plant.id)}
                            size="small"
                            color="primary"
                            disabled={isAllSelected}
                          />
                          <SpeedIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                          <Typography variant="body2">
                            <strong>{meter.meter_number}</strong>
                            {meter.c_nome ? ` — ${meter.c_nome}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </AccordionDetails>
                  )}
                </Accordion>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Bottoni azione */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={() => setPreviewOpen(true)}
        >
          Anteprima
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/crm/comunicazioni')}
        >
          Annulla
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Salvataggio...' : (isEdit ? 'Salva Modifiche' : 'Crea Comunicazione')}
        </Button>
      </Box>

      {/* Dialog Anteprima */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon color="primary" />
            <Typography fontWeight={700}>Anteprima comunicazione</Typography>
          </Box>
          <IconButton size="small" onClick={() => setPreviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#f5f5f5' }}>
          {/* Simulazione pagina A4 */}
          <Box sx={{
            maxWidth: 794,
            mx: 'auto',
            my: 2,
            bgcolor: 'white',
            boxShadow: 3,
            borderRadius: 1,
            overflow: 'hidden',
            fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif"
          }}>
            {/* Header azienda simulato */}
            <Box sx={{
              borderBottom: '3px solid #0ea5e9',
              px: 3,
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-end',
              bgcolor: 'white'
            }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Nome Azienda</Typography>
                <Typography sx={{ fontSize: 12, color: '#334155' }}>Via Esempio 1 - 25100 Brescia (BS)</Typography>
              </Box>
            </Box>

            {/* Titolo comunicazione */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                display: 'inline-block',
                mb: 2
              }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                  📢 {titolo || 'Titolo comunicazione'}
                </Typography>
              </Box>

              {/* Corpo comunicazione */}
              <Box
                sx={{
                  fontSize: 13,
                  lineHeight: 1.8,
                  color: '#1e293b',
                  '& p': { mb: 1 },
                  '& ul, & ol': { pl: 3, mb: 1 },
                  '& h1': { fontSize: 20, mb: 1 },
                  '& h2': { fontSize: 17, mb: 1 },
                  '& h3': { fontSize: 14, mb: 0.5 },
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                  '& table': { borderCollapse: 'collapse', width: '100%', mb: 1 },
                  '& td, & th': { border: '1px solid #e2e8f0', p: '4px 8px' },
                  '& th': { bgcolor: '#f1f5f9', fontWeight: 600 },
                  '& blockquote': { borderLeft: '3px solid #0ea5e9', ml: 0, pl: 2, color: '#475569' },
                }}
                dangerouslySetInnerHTML={{ __html: contenuto || '<p style="color:#94a3b8">Nessun contenuto inserito</p>' }}
              />
            </Box>

            {/* Box QR simulato */}
            {mostraQrcode && (
              <Box sx={{
                mx: 3,
                mb: 2,
                border: '2px solid #25D366',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#f0fdf4'
              }}>
                <Box sx={{
                  width: 72,
                  height: 72,
                  bgcolor: '#e2e8f0',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <QrCode2Icon sx={{ fontSize: 48, color: '#64748b' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#15803d', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    💬 WHATSAPP
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#166534', mb: 0.5 }}>Registra il tuo numero!</Typography>
                  <Typography sx={{ fontSize: 11, color: '#374151', lineHeight: 1.5 }}>
                    Scansiona il QR con il telefono per ricevere<br />comunicazioni su WhatsApp.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Footer contatti simulato */}
            <Box sx={{
              background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
              color: 'white',
              px: 3,
              py: 1,
              display: 'flex',
              justifyContent: 'center',
              gap: 3,
              fontSize: 11
            }}>
              <Typography sx={{ fontSize: 11, color: 'white' }}>✉️ info@azienda.it</Typography>
              <Typography sx={{ fontSize: 11, color: 'white' }}>📞 030 000000</Typography>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', pb: 2 }}>
            L'anteprima è indicativa. Il PDF finale includerà i dati reali dell'azienda e del cliente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunicationForm;
